import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Activity, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { daysUntilSunset } from "@/lib/sunset-dates";
import { useIntegrationHealth, type SmokeLogRow } from "@/hooks/useIntegrationHealth";
import { PageTour } from "@/components/tour/PageTour";
import { integrationHealthSteps } from "@/lib/tour-steps";

const PROVIDER_LABEL: Record<SmokeLogRow['provider'], string> = {
  ghl: 'GoHighLevel',
  meta: 'Meta Marketing',
  google_ads: 'Google Ads',
};

function getStatusBadge(status: SmokeLogRow['status']) {
  switch (status) {
    case 'ok':        return <BadgeVenture variant="success">ok</BadgeVenture>;
    case 'failed':    return <BadgeVenture variant="error">failed</BadgeVenture>;
    case 'timed_out': return <BadgeVenture variant="warning">timed out</BadgeVenture>;
  }
}

function SunsetCell({ date }: { date: string | null }) {
  if (!date) return <span className="text-muted-foreground">—</span>;
  const days = daysUntilSunset(date);
  if (days !== null && days <= 90) {
    return (
      <span className="text-warning flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" />
        {date} · {days} days
      </span>
    );
  }
  return <span className="text-foreground">{date}</span>;
}

export default function IntegrationHealth() {
  const qc = useQueryClient();
  const { data: badge } = useIntegrationHealth();

  const logsQuery = useQuery<SmokeLogRow[], Error>({
    queryKey: ['integration_smoke_logs', 'last_30_days'],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      // Cast: integration_smoke_logs is added by migration 20260423000001 but the
      // generated Database types haven't been regenerated yet.
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            gte: (col: string, val: string) => {
              order: (col: string, opts: { ascending: boolean }) => Promise<{ data: SmokeLogRow[] | null; error: Error | null }>;
            };
          };
        };
      })
        .from('integration_smoke_logs')
        .select('id, provider, status, latency_ms, error_snippet, api_version, sunset_date, ran_at')
        .gte('ran_at', cutoff)
        .order('ran_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as SmokeLogRow[];
    },
    staleTime: 30_000,
  });

  const runSmoke = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('integrations-smoke-cron', { body: { source: 'manual' } });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integration_smoke_logs'] });
    },
    onError: (err: Error) => {
      toast.error("Couldn't run smoke test", { description: err.message });
    },
  });

  const rows = logsQuery.data ?? [];

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={integrationHealthSteps} tourId="integration-health" />
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold flex items-center gap-2">
          <Activity className="w-6 h-6 text-secondary" />
          Integration Health
        </h1>
        <p className="text-sm text-muted-foreground">
          Daily smoke tests for GoHighLevel, Meta Marketing, and Google Ads
        </p>
      </div>

      {/* Per-provider summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-tour-id="integration-health-summary">
        {(['ghl', 'meta', 'google_ads'] as const).map((p) => {
          const state = badge?.providers[p];
          return (
            <VentureCard key={p}>
              <VentureCardHeader>
                <VentureCardTitle>{PROVIDER_LABEL[p]}</VentureCardTitle>
              </VentureCardHeader>
              <VentureCardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  {state && state.status !== 'unknown'
                    ? getStatusBadge(state.status as SmokeLogRow['status'])
                    : <span className="text-muted-foreground text-sm">unknown</span>}
                  {state?.ran_at && (
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(state.ran_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  API version: {state?.api_version ?? '—'}
                </p>
                <p className="text-xs">
                  Sunset: <SunsetCell date={state?.sunset_date ?? null} />
                </p>
              </VentureCardContent>
            </VentureCard>
          );
        })}
      </div>

      {/* Last 30 days history table */}
      <VentureCard data-tour-id="integration-health-history">
        <VentureCardHeader className="flex-row items-center justify-between space-y-0">
          <VentureCardTitle>Last 30 days</VentureCardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => runSmoke.mutate()}
            disabled={runSmoke.isPending}
            data-tour-id="integration-health-run-now"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${runSmoke.isPending ? 'animate-spin' : ''}`} />
            {runSmoke.isPending ? 'Running…' : 'Run smoke now'}
          </Button>
        </VentureCardHeader>
        <VentureCardContent>
          {logsQuery.isError ? (
            <div className="py-8 text-center">
              <p className="text-base font-semibold">Couldn't load smoke logs</p>
              <p className="text-sm text-muted-foreground">
                Refresh to retry. If this persists, check the integrations-smoke-cron edge function logs.
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-base font-semibold">No smoke runs recorded yet</p>
              <p className="text-sm text-muted-foreground">
                The daily cron runs at 5:00 AM UTC. First results will appear after the next run.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">Run at</th>
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">Provider</th>
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">Latency</th>
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">API version</th>
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">Sunset</th>
                    <th className="text-left py-3 px-4 text-sm font-normal text-muted-foreground">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4 text-sm">
                        {new Date(r.ran_at).toISOString().replace('T', ' ').slice(0, 19)} UTC
                      </td>
                      <td className="py-3 px-4 text-sm">{PROVIDER_LABEL[r.provider]}</td>
                      <td className="py-3 px-4">{getStatusBadge(r.status)}</td>
                      <td className="py-3 px-4 text-sm">
                        {r.latency_ms != null ? `${r.latency_ms}ms` : '—'}
                      </td>
                      <td className="py-3 px-4 text-sm">{r.api_version ?? '—'}</td>
                      <td className="py-3 px-4 text-sm">
                        <SunsetCell date={r.sunset_date} />
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {r.error_snippet ? (
                          <span className="font-mono text-xs" title={r.error_snippet}>
                            {r.error_snippet.length > 80
                              ? r.error_snippet.slice(0, 80) + '…'
                              : r.error_snippet}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
